import { Component, forwardRef, OnInit } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { OrderCustomerClient, OrderCustomerSearchModel } from '../../api-connector/sso-connector';
import { BaseAutocompleteControl } from '../base-autocomplete-control';

export interface OrderOption {
    id: string; // Order IDs can be enormous, usually handled as string in FE
    code: string;
    customerId?: string;
    customerName: string;
    orderTotal: number;
    createdTime: Date;
}

@Component({
    selector: 'app-order-search-control',
    templateUrl: './order-search-control.component.html',
    styleUrls: ['./order-search-control.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => OrderSearchControlComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => OrderSearchControlComponent),
            multi: true,
        }
    ]
})
export class OrderSearchControlComponent extends BaseAutocompleteControl<OrderOption> implements OnInit {

    constructor(private orderClient: OrderCustomerClient) {
        super();
    }

    ngOnInit(): void {
        if (!this.placeholder || this.placeholder === '---Lựa chọn---') {
            this.placeholder = 'Tìm mã đơn hàng hoặc tên khách hàng...';
        }
        super.ngOnInit();
    }

    protected fetchItems(searchValue: string): Observable<OrderOption[]> {
        const request = new OrderCustomerSearchModel({
            keySearch: searchValue || '',
            pageIndex: 0,
            pageSize: 20, // Default page size
            isOrdered: false
        });

        return this.orderClient.listOrderCustomer(request).pipe(
            map((response: any) => {
                if (!response?.items) return [];
                return response.items.map((order: any) => ({
                    id: order.id,
                    code: order.code,
                    customerId: order.customerId,
                    customerName: order.customerName,
                    orderTotal: order.orderTotal,
                    createdTime: order.createdTime ? new Date(order.createdTime) : new Date()
                }));
            }),
            catchError(() => of([]))
        );
    }

    getDisplayValue(item: OrderOption | null): string {
        return item?.code || '';
    }

    protected isValidItem(value: any): boolean {
        return value && value.id && value.code;
    }

    writeValue(obj: any): void {
        if (this.isValidItem(obj)) {
            super.writeValue(obj);
        } else if (obj) {
            // Assume obj is ID or Code
            const request = new OrderCustomerSearchModel({
                keySearch: obj.toString(),
                pageIndex: 0,
                pageSize: 1,
                isOrdered: false
            });

            this.orderClient.listOrderCustomer(request).pipe(
                map((response: any) => {
                    if (!response?.items || response.items.length === 0) return null;
                    const order = response.items[0];
                    return {
                        id: order.id,
                        code: order.code,
                        customerId: order.customerId,
                        customerName: order.customerName,
                        orderTotal: order.orderTotal,
                        createdTime: order.createdTime ? new Date(order.createdTime) : new Date()
                    };
                }),
                catchError(() => of(null))
            ).subscribe((item: any) => {
                if (item) {
                    super.writeValue(item);
                }
            });
        } else {
            super.writeValue(obj);
        }
    }

    formatCurrency(value: number): string {
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
    }

    formatDateTime(date: Date): string {
        if (!date) return '';
        return new Date(date).toLocaleString('vi-VN');
    }
}
